/**
 * Botón "Nueva conversación" (pedido de Diana Soler, reunión 2026-07-21):
 * reinicia el hilo con Galia para poder repetir un ejercicio desde cero.
 * Hermano visual de ContentsButton/SettingsButton (reusa .info-button).
 *
 * Confirmación en dos pasos para evitar borrados accidentales: el primer
 * clic abre una burbuja de confirmación sobre el botón; "Sí" ejecuta el
 * reinicio, "Cancelar" o un clic fuera la cierran.
 */

import { useEffect, useRef, useState } from 'react';
import './InfoButton.css'; // reusa .info-button para el estilo base
import './NewConversationButton.css';

interface NewConversationButtonProps {
  onReset: () => Promise<void> | void;
  /** Modo profesor (aide-prof): textos en francés. Por defecto español. */
  aideProf?: boolean;
}

export function NewConversationButton({ onReset, aideProf = false }: NewConversationButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Clic/tap fuera de la burbuja → cancelar
  useEffect(() => {
    if (!confirming) return;
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setConfirming(false);
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [confirming]);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onReset();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const t = aideProf
    ? {
        btn: 'Nouvelle conversation',
        q: 'Recommencer à zéro ?',
        hint: 'La conversation actuelle sera effacée du chat.',
        yes: 'Oui, recommencer',
        no: 'Annuler',
      }
    : {
        btn: 'Nueva conversación',
        q: '¿Empezar de nuevo?',
        hint: 'La conversación actual se borrará del chat.',
        yes: 'Sí, empezar de nuevo',
        no: 'Cancelar',
      };

  return (
    <div className="new-conversation-wrap" ref={wrapRef}>
      {confirming && (
        <div className="new-conversation-confirm" role="alertdialog" aria-label={t.q}>
          <p className="new-conversation-confirm-title">{t.q}</p>
          <p className="new-conversation-confirm-hint">{t.hint}</p>
          <div className="new-conversation-confirm-actions">
            <button type="button" className="new-conversation-yes" onClick={handleConfirm} disabled={busy}>
              {busy ? '…' : t.yes}
            </button>
            <button type="button" className="new-conversation-no" onClick={() => setConfirming(false)} disabled={busy}>
              {t.no}
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        className={`info-button new-conversation-button ${confirming ? 'confirming' : ''}`}
        onClick={() => setConfirming((v) => !v)}
        aria-label={t.btn}
        title={t.btn}
      >
        {/* Burbuja de chat con flecha circular (reiniciar el hilo) */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M9.3 8.5a3.2 3.2 0 0 1 5.9 1.2" />
          <polyline points="15.6 6.9 15.2 9.7 12.4 9.3" />
          <path d="M14.7 12.3a3.2 3.2 0 0 1-5.9-1.2" />
          <polyline points="8.4 13.9 8.8 11.1 11.6 11.5" />
        </svg>
      </button>
    </div>
  );
}
